/**
 * StudentWeeklyStrip — the "This week" stat row on the student dashboard.
 *
 * Aligns the student dashboard to the parent dashboard's "This week" strip pattern.
 * Three honest tiles, all from data the dashboard already returns (no backend change):
 *   1. Sessions this week  — week.sessionsThisWeek
 *   2. First-try wins      — week.firstTryWinsThisWeek (+ deltaVsLastWeek)
 *   3. Days practised      — distinct days with pebbles in the last 7 (from dailyPebbles)
 *
 * Deliberately does NOT use week.bestWeekFirstTryWins — the backend returns that as a
 * hardcoded dummy value (dashboard_service.py), so surfacing it would show fake data.
 * "Days practised" is derived client-side from the real dailyPebbles instead.
 */

import type { DashboardWeek, DailyPebbles } from '@/types/dashboard';

export interface StudentWeeklyStripProps {
  week: DashboardWeek;
  dailyPebbles: DailyPebbles[];
}

/** Count distinct days with pebbles earned in the most recent 7 daily entries. */
function computeDaysPractised(dailyPebbles: DailyPebbles[]): number {
  return dailyPebbles
    .slice(0, 7)
    .filter((d) => (d.pebbles ?? 0) > 0).length;
}

interface MetricProps {
  icon: string;
  iconBg: string;
  value: React.ReactNode;
  caption: string;
}

function Metric({ icon, iconBg, value, caption }: MetricProps) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-otter-sand bg-white px-4 py-3.5 shadow-sm">
      <span
        aria-hidden="true"
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-lg ${iconBg}`}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <div className="font-display text-xl font-bold leading-none text-otter-earth-800">{value}</div>
        <div className="mt-1 text-[11.5px] font-semibold text-gray-500">{caption}</div>
      </div>
    </div>
  );
}

export function StudentWeeklyStrip({ week, dailyPebbles }: StudentWeeklyStripProps) {
  const daysPractised = computeDaysPractised(dailyPebbles);
  const delta = week.deltaVsLastWeek;

  return (
    <div className="mb-4 md:mb-6">
      <p className="mb-3 flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wider text-pebbles-400">
        <span aria-hidden="true">✨</span> This week
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Metric
          icon="📚"
          iconBg="bg-tutor-50"
          value={week.sessionsThisWeek ?? 0}
          caption="Sessions this week"
        />
        <Metric
          icon="⭐"
          iconBg="bg-amber-50"
          value={
            <>
              {week.firstTryWinsThisWeek ?? 0}{' '}
              {delta != null && delta !== 0 && (
                <span
                  className={`font-body text-xs font-extrabold ${
                    delta > 0 ? 'text-student-600' : 'text-red-500'
                  }`}
                >
                  {delta > 0 ? `+${delta}` : delta}
                </span>
              )}
            </>
          }
          caption="First-try wins"
        />
        <Metric
          icon="📅"
          iconBg="bg-[#e9f6f3]"
          value={
            <>
              {daysPractised} <span className="font-body text-[13px] font-bold text-gray-400">of 7 days</span>
            </>
          }
          caption="Days practised"
        />
      </div>
    </div>
  );
}
